import React, {
  memo,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Box, useTheme } from '@mui/material';
import { Button } from '../../components/button/index.ts';
import { useObservedWidth } from '../../assets/helpers/hooks/use-observed-width.ts';

export type IsoDate = string;

export type FlightGraphicPoint = {
  date: IsoDate;
  x: number;
  y: number;
  z: number;
};

type FlightGraphicProps = {
  data: FlightGraphicPoint[];
  height?: number;
  pointDurationMs?: number;
  headroom?: number;
  autoPlay?: boolean;
  showToggleButton?: boolean;
  buttonLabel?: string;
  className?: string;
};

type Point2D = {
  x: number;
  y: number;
};

type FlightPathLayout = {
  viewBoxWidth: number;
  viewBoxHeight: number;
  centerX: number;
  fieldTopY: number;
  fieldBottomY: number;
  fieldHeight: number;
  fieldTopWidth: number;
  fieldBottomWidth: number;
  leftAxisX: number;
  labelX: number;
};

const DEFAULT_HEIGHT = 310;
const DEFAULT_HEADROOM = 75;
const DEFAULT_POINT_DURATION_MS = 180;
const DEFAULT_TICK_COUNT = 4;
const STRIPES_COUNT = 8;

const BACKGROUND_COLOR = '#F2F8ED';
const FIELD_DARK = '#CFDEC1';
const FIELD_LIGHT = '#E5F0DB';

/**
 * Tuning values to match the mockup closer.
 * You can tweak only these 3 constants if you want the curve
 * a bit flatter / higher / more left-right shifted.
 */
const LATERAL_SPREAD_RATIO = 0.18;
const DEPTH_VERTICAL_RATIO = 0.52;
const HEIGHT_LIFT_RATIO = 0.34;

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);
const lerp = (from: number, to: number, t: number) => from + (to - from) * t;

const getNiceStep = (value: number) => {
  if (value <= 0) return 50;

  const magnitude = 10 ** Math.floor(Math.log10(value));
  const normalized = value / magnitude;

  if (normalized <= 1) return magnitude;
  if (normalized <= 1.25) return 1.25 * magnitude;
  if (normalized <= 1.5) return 1.5 * magnitude;
  if (normalized <= 2) return 2 * magnitude;
  if (normalized <= 2.5) return 2.5 * magnitude;
  if (normalized <= 5) return 5 * magnitude;

  return 10 * magnitude;
};

const getDistanceScaleConfig = (
  data: FlightGraphicPoint[],
  tickCount: number,
  headroom: number,
) => {
  const maxDistance = data.reduce((acc, point) => Math.max(acc, point.z), 0);
  const targetMax = Math.max(100, maxDistance + headroom);
  const step = getNiceStep(targetMax / tickCount);
  const scaleMax = step * tickCount;

  return {
    step,
    scaleMax,
    ticks: Array.from({ length: tickCount }, (_, index) => step * (index + 1)),
  };
};

const getProgressRatio = (progress: number, pointsCount: number) => {
  if (pointsCount <= 1) return 0;
  return clamp(progress / (pointsCount - 1), 0, 1);
};

const getMaxAbsX = (points: FlightGraphicPoint[]) => {
  return points.reduce((acc, point) => Math.max(acc, Math.abs(point.x)), 0);
};

const getMaxY = (points: FlightGraphicPoint[]) => {
  return points.reduce((acc, point) => Math.max(acc, point.y), 0);
};

const buildSmoothPathFromPoints = (points: Point2D[]) => {
  if (!points.length) return '';
  if (points.length === 1) return `M ${points[0].x} ${points[0].y}`;
  if (points.length === 2) {
    return `M ${points[0].x} ${points[0].y} L ${points[1].x} ${points[1].y}`;
  }

  const tension = 0.72;
  let d = `M ${points[0].x} ${points[0].y}`;

  for (let i = 0; i < points.length - 1; i += 1) {
    const p0 = points[i - 1] ?? points[i];
    const p1 = points[i];
    const p2 = points[i + 1];
    const p3 = points[i + 2] ?? p2;

    const cp1x = p1.x + ((p2.x - p0.x) * tension) / 6;
    const cp1y = p1.y + ((p2.y - p0.y) * tension) / 6;

    const cp2x = p2.x - ((p3.x - p1.x) * tension) / 6;
    const cp2y = p2.y - ((p3.y - p1.y) * tension) / 6;

    d += ` C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${p2.x} ${p2.y}`;
  }

  return d;
};

const projectFlightPointTo2D = ({
                                  point,
                                  layout,
                                  distanceScaleMax,
                                  maxAbsX,
                                  maxY,
                                  getHalfFieldWidthByRatio,
                                }: {
  point: FlightGraphicPoint;
  layout: FlightPathLayout;
  distanceScaleMax: number;
  maxAbsX: number;
  maxY: number;
  getHalfFieldWidthByRatio: (ratio: number) => number;
}): Point2D => {
  const depthRatio = distanceScaleMax > 0 ? clamp(point.z / distanceScaleMax, 0, 1) : 0;
  const heightRatio = maxY > 0 ? clamp(point.y / maxY, 0, 1) : 0;
  const lateralRatio = maxAbsX > 0 ? clamp(point.x / maxAbsX, -1, 1) : 0;

  const halfWidthAtDepth = getHalfFieldWidthByRatio(depthRatio);

  const projectedX = layout.centerX + lateralRatio * halfWidthAtDepth * LATERAL_SPREAD_RATIO;

  const depthOffsetY = depthRatio * layout.fieldHeight * DEPTH_VERTICAL_RATIO;
  const heightLiftY = heightRatio * layout.fieldHeight * HEIGHT_LIFT_RATIO;

  const projectedY = clamp(
    layout.fieldBottomY - depthOffsetY - heightLiftY,
    layout.fieldTopY + 6,
    layout.fieldBottomY,
  );

  return {
    x: projectedX,
    y: projectedY,
  };
};

export const FlightGraphic = memo((props: FlightGraphicProps) => {
  const {
    data,
    height = DEFAULT_HEIGHT,
    pointDurationMs = DEFAULT_POINT_DURATION_MS,
    headroom = DEFAULT_HEADROOM,
    autoPlay = false,
    showToggleButton = true,
    buttonLabel = 'Activate Radar',
    className,
  } = props;

  const { palette, typography } = useTheme();
  const { ref: containerRef, width: containerWidth } = useObservedWidth<HTMLDivElement>();

  const TRAJECTORY_COLOR = palette.accent_text;
  const GRID_COLOR = palette.light_dark;

  const clipPathId = useId().replace(/:/g, '_');
  const pathRef = useRef<SVGPathElement | null>(null);

  const sortedData = useMemo(() => {
    return [...data].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  }, [data]);

  const [isPlaying, setIsPlaying] = useState(autoPlay);
  const [progress, setProgress] = useState(0);

  const progressRatioRef = useRef(0);
  const animationStartRef = useRef<number | null>(null);

  useEffect(() => {
    setIsPlaying(autoPlay);
  }, [autoPlay]);

  useEffect(() => {
    if (!sortedData.length) {
      setProgress(0);
      return;
    }

    if (sortedData.length === 1) {
      setProgress(0);
      return;
    }

    if (!isPlaying) return;

    const segmentCount = sortedData.length - 1;
    const cycleDuration = segmentCount * pointDurationMs;

    let frameId = 0;

    const animate = (now: number) => {
      if (animationStartRef.current === null) {
        animationStartRef.current = now - progressRatioRef.current * cycleDuration;
      }

      const elapsed = now - animationStartRef.current;
      const nextRatio = (elapsed % cycleDuration) / cycleDuration;
      const nextProgress = nextRatio * segmentCount;

      setProgress(nextProgress);
      frameId = window.requestAnimationFrame(animate);
    };

    frameId = window.requestAnimationFrame(animate);

    return () => {
      window.cancelAnimationFrame(frameId);
    };
  }, [isPlaying, pointDurationMs, sortedData]);

  const layout = useMemo<FlightPathLayout>(() => {
    const viewBoxWidth = Math.max(containerWidth, 320);
    const viewBoxHeight = height;
    const centerX = viewBoxWidth / 2;

    const fieldTopY = 22;
    const fieldBottomY = viewBoxHeight - 10;
    const fieldHeight = fieldBottomY - fieldTopY;

    const fieldBottomWidth = clamp(viewBoxWidth * 0.66, 260, viewBoxWidth - 84);
    const fieldTopWidth = clamp(viewBoxWidth * 0.24, 140, fieldBottomWidth - 100);

    return {
      viewBoxWidth,
      viewBoxHeight,
      centerX,
      fieldTopY,
      fieldBottomY,
      fieldHeight,
      fieldTopWidth,
      fieldBottomWidth,
      leftAxisX: 52,
      labelX: 18,
    };
  }, [containerWidth, height]);

  const distanceScaleConfig = useMemo(() => {
    return getDistanceScaleConfig(sortedData, DEFAULT_TICK_COUNT, headroom);
  }, [sortedData, headroom]);

  const maxAbsX = useMemo(() => getMaxAbsX(sortedData), [sortedData]);
  const maxY = useMemo(() => getMaxY(sortedData), [sortedData]);

  const getHalfFieldWidthByRatio = (ratio: number) => {
    const width = lerp(layout.fieldBottomWidth, layout.fieldTopWidth, ratio);
    return width / 2;
  };

  const projectedPoints = useMemo(() => {
    return sortedData.map((point) =>
      projectFlightPointTo2D({
        point,
        layout,
        distanceScaleMax: distanceScaleConfig.scaleMax,
        maxAbsX,
        maxY,
        getHalfFieldWidthByRatio,
      }),
    );
  }, [sortedData, layout, distanceScaleConfig.scaleMax, maxAbsX, maxY]);

  const pathD = useMemo(() => {
    return buildSmoothPathFromPoints(projectedPoints);
  }, [projectedPoints]);

  const progressRatio = useMemo(() => {
    return getProgressRatio(progress, sortedData.length);
  }, [progress, sortedData.length]);

  const fallbackBallPoint = projectedPoints[0] ?? {
    x: layout.centerX,
    y: layout.fieldBottomY,
  };

  const [currentBallPoint, setCurrentBallPoint] = useState<Point2D>(fallbackBallPoint);

  useLayoutEffect(() => {
    const pathNode = pathRef.current;

    if (!pathNode || !pathD) {
      setCurrentBallPoint(fallbackBallPoint);
      return;
    }

    const totalLength = pathNode.getTotalLength();
    const pointAtLength = pathNode.getPointAtLength(totalLength * progressRatio);

    setCurrentBallPoint({
      x: pointAtLength.x,
      y: pointAtLength.y,
    });
  }, [pathD, progressRatio, fallbackBallPoint]);

  const handleTogglePlayback = () => {
    if (sortedData.length <= 1) return;

    const segmentCount = sortedData.length - 1;
    progressRatioRef.current = segmentCount > 0 ? progress / segmentCount : 0;
    animationStartRef.current = null;
    setIsPlaying((prev) => !prev);
  };

  if (!sortedData.length) {
    return (
      <Box
        className={className}
        sx={{
          width: '100%',
          height,
          borderRadius: 2,
          bgcolor: BACKGROUND_COLOR,
        }}
      />
    );
  }

  return (
    <Box
      ref={containerRef}
      className={className}
      sx={{
        position: 'relative',
        width: '100%',
        height,
        borderRadius: 2,
        overflow: 'hidden',
        bgcolor: BACKGROUND_COLOR,
      }}
    >
      {showToggleButton && (
        <Button
          variantStyle="secondary"
          size="small"
          onClick={handleTogglePlayback}
          sx={{
            position: 'absolute',
            top: 14,
            right: 18,
            zIndex: 2,
            width: '102px',
          }}
        >
          {isPlaying ? 'Pause Radar' : buttonLabel}
        </Button>
      )}

      <svg
        width={layout.viewBoxWidth}
        height={layout.viewBoxHeight}
        viewBox={`0 0 ${layout.viewBoxWidth} ${layout.viewBoxHeight}`}
        style={{ display: 'block' }}
      >
        <defs>
          <clipPath id={clipPathId}>
            <path
              d={`
                M ${layout.centerX - layout.fieldTopWidth / 2} ${layout.fieldTopY}
                L ${layout.centerX + layout.fieldTopWidth / 2} ${layout.fieldTopY}
                L ${layout.centerX + layout.fieldBottomWidth / 2} ${layout.fieldBottomY}
                L ${layout.centerX - layout.fieldBottomWidth / 2} ${layout.fieldBottomY}
                Z
              `}
            />
          </clipPath>
        </defs>

        <g clipPath={`url(#${clipPathId})`}>
          {Array.from({ length: STRIPES_COUNT }).map((_, index) => {
            const stripeHeight = layout.fieldHeight / STRIPES_COUNT;
            const stripeY = layout.fieldTopY + index * stripeHeight;

            return (
              <rect
                key={index}
                x={layout.centerX - layout.fieldBottomWidth / 2 - 30}
                y={stripeY}
                width={layout.fieldBottomWidth + 60}
                height={stripeHeight}
                fill={index % 2 === 0 ? FIELD_DARK : FIELD_LIGHT}
              />
            );
          })}

          {pathD && (
            <path
              ref={pathRef}
              d={pathD}
              fill="none"
              stroke={TRAJECTORY_COLOR}
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              vectorEffect="non-scaling-stroke"
            />
          )}

          <circle
            cx={currentBallPoint.x}
            cy={currentBallPoint.y}
            r="8"
            fill={palette.white_100}
            stroke={TRAJECTORY_COLOR}
            strokeWidth="2"
            vectorEffect="non-scaling-stroke"
          />
        </g>

        {distanceScaleConfig.ticks.map((tickValue) => {
          const ratio = tickValue / distanceScaleConfig.scaleMax;
          const y = layout.fieldBottomY - ratio * layout.fieldHeight;
          const fieldLeftX = layout.centerX - getHalfFieldWidthByRatio(ratio);

          return (
            <g key={tickValue}>
              <text
                x={layout.labelX}
                y={y + 5}
                fill={GRID_COLOR}
                fontFamily={typography.fontFamily}
                fontSize="14"
                fontWeight={400}
              >
                {Math.round(tickValue)}
              </text>

              <line
                x1={layout.leftAxisX}
                y1={y}
                x2={fieldLeftX - 10}
                y2={y}
                stroke={GRID_COLOR}
                strokeDasharray="4 6"
                vectorEffect="non-scaling-stroke"
              />
            </g>
          );
        })}
      </svg>
    </Box>
  );
});

FlightGraphic.displayName = 'FlightGraphic';